import { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { emailImagesAPI } from '../../api/endpoints';
import {
  cachedEmailImages,
  prewarmEmailImages,
  registerEmailImage,
  unregisterEmailImage,
  blobForImage,
} from '../../utils/emailImages';
import { downscaleImageFile, MAX_IMAGE_BYTES } from '../../utils/imageUpload';
import type { EmailImage } from '../../types';

interface ImagePickerProps {
  /** Hands back the chosen image. The editor turns it into an `<img>` node. */
  onPick: (image: EmailImage) => void;
  onClose: () => void;
}

/**
 * The mail editor's image library popover (docs/EMAIL_IMAGES.md).
 *
 * Separate from `SnippetMenu`, which is `/`-triggered and ranks text; this is a toolbar popover
 * with a thumbnail grid. Thumbnails are the blob URLs the library pre-warmed, so opening it
 * fetches nothing.
 */
export function ImagePicker({ onPick, onClose }: ImagePickerProps) {
  const [images, setImages] = useState<EmailImage[]>(cachedEmailImages);
  const [isUploading, setIsUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void prewarmEmailImages().then(() => setImages(cachedEmailImages()));
  }, []);

  // Close on outside click or Escape, like the other popovers in here
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const upload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        const { file: downscaled, width, height } = await downscaleImageFile(file);
        const { data } = await emailImagesAPI.upload(downscaled, width, height);
        registerEmailImage(data);
        setImages(cachedEmailImages());
        onPick(data);
      } catch (error) {
        const detail =
          (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          'Could not upload that image';
        toast.error(detail);
      } finally {
        setIsUploading(false);
      }
    },
    [onPick]
  );

  const remove = async (image: EmailImage) => {
    try {
      const { data } = await emailImagesAPI.remove(image.id);
      const stillUsed = data.usage.templates + data.usage.snippets + data.usage.scheduled + data.usage.campaigns;
      unregisterEmailImage(image.id);
      setImages(cachedEmailImages());
      if (stillUsed) {
        // Soft delete: the bytes stay, so what is already written keeps working
        toast(
          `Removed from the library. It is still used in ${[
            data.usage.templates && `${data.usage.templates} template(s)`,
            data.usage.snippets && `${data.usage.snippets} snippet(s)`,
            data.usage.scheduled && `${data.usage.scheduled} unsent email(s)`,
            data.usage.campaigns && `${data.usage.campaigns} running campaign(s)`,
          ]
            .filter(Boolean)
            .join(', ')}, which will still show it.`,
          { duration: 6000 }
        );
      }
    } catch {
      toast.error('Could not remove that image');
    }
  };

  return (
    <div
      ref={container}
      className="absolute z-50 mt-1 w-80 rounded-lg border border-dark-border bg-dark-surface shadow-xl p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-dark-text-secondary uppercase tracking-wide">Images</span>
        <button type="button" onClick={onClose} className="text-dark-text-muted hover:text-dark-text-primary">
          <X className="w-4 h-4" />
        </button>
      </div>

      {images.length === 0 ? (
        <p className="text-sm text-dark-text-muted py-3">
          No images yet. Upload one, or paste or drag an image into the editor.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto">
          {images.map((image) => {
            const src = blobForImage(image.id);
            return (
              <div key={image.id} className="relative group">
                <button
                  type="button"
                  onClick={() => onPick(image)}
                  title={`${image.filename} — ${Math.round(image.bytes / 1024)} KB`}
                  className="block w-full aspect-square rounded-sm border border-dark-border overflow-hidden bg-dark-elevated hover:border-primary-500 transition-colors cursor-pointer"
                >
                  {src ? (
                    <img src={src} alt={image.filename} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-dark-text-muted">…</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => void remove(image)}
                  title="Remove from library"
                  className="absolute top-1 right-1 p-1 rounded-sm bg-dark-bg/80 text-dark-text-muted opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity cursor-pointer"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void upload(file);
        }}
      />
      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        disabled={isUploading}
        className={clsx(
          'mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors cursor-pointer',
          'bg-dark-elevated text-dark-text-secondary hover:bg-dark-bg hover:text-dark-text-primary',
          isUploading && 'opacity-60 cursor-wait'
        )}
      >
        <Upload className="w-4 h-4" />
        {isUploading ? 'Uploading…' : 'Upload an image'}
      </button>
      <p className="mt-2 text-[11px] text-dark-text-muted">
        Resized to 1600px before upload, max {MAX_IMAGE_BYTES / (1024 * 1024)}MB.
      </p>
    </div>
  );
}
